/**
 * Оптимизатор рендеринга боя
 * Переход от полной перерисовки (innerHTML) к точечным обновлениям элементов
 */

/**
 * Создает менеджер для оптимизированного рендеринга боя
 */
export function createBattleRenderOptimizer() {
  // Кэш элементов DOM
  const elementCache = new Map();
  
  // Кэш последних значений для сравнения
  const lastValues = new Map();
  
  /**
   * Получает или создает элемент из кэша
   */
  function getCachedElement(selector, createFn) {
    if (elementCache.has(selector)) {
      return elementCache.get(selector);
    }
    const element = document.querySelector(selector);
    if (element) {
      elementCache.set(selector, element);
      return element;
    }
    if (createFn) {
      const newElement = createFn();
      if (newElement) {
        elementCache.set(selector, newElement);
        return newElement;
      }
    }
    return null;
  }
  
  /**
   * Обновляет текст элемента только если значение изменилось
   */
  function updateText(selector, newValue, createFn) {
    const element = getCachedElement(selector, createFn);
    if (!element) return false;
    
    const key = `text:${selector}`;
    const lastValue = lastValues.get(key);
    
    if (lastValue !== newValue) {
      element.textContent = newValue;
      lastValues.set(key, newValue);
      return true;
    }
    return false;
  }
  
  /**
   * Обновляет HTML элемента только если значение изменилось
   */
  function updateHTML(selector, newHTML, createFn) {
    const element = getCachedElement(selector, createFn);
    if (!element) return false;
    
    const key = `html:${selector}`;
    const lastValue = lastValues.get(key);
    
    if (lastValue !== newHTML) {
      element.innerHTML = newHTML;
      lastValues.set(key, newHTML);
      return true;
    }
    return false;
  }
  
  /**
   * Обновляет класс элемента
   */
  function updateClass(selector, className, add = true) {
    const element = getCachedElement(selector);
    if (!element) return false;
    
    const key = `class:${selector}:${className}`;
    const lastValue = lastValues.get(key);
    
    if (lastValue !== add) {
      if (add) {
        element.classList.add(className);
      } else {
        element.classList.remove(className);
      }
      lastValues.set(key, add);
      return true;
    }
    return false;
  }
  
  /**
   * Обновляет стиль элемента
   */
  function updateStyle(selector, property, value) {
    const element = getCachedElement(selector);
    if (!element) return false;
    
    const key = `style:${selector}:${property}`;
    const lastValue = lastValues.get(key);
    
    if (lastValue !== value) {
      element.style[property] = value;
      lastValues.set(key, value);
      return true;
    }
    return false;
  }
  
  /**
   * Обновляет атрибут элемента
   */
  function updateAttribute(selector, attribute, value) {
    const element = getCachedElement(selector);
    if (!element) return false;
    
    const key = `attr:${selector}:${attribute}`;
    const lastValue = lastValues.get(key);
    
    if (lastValue !== value) {
      element.setAttribute(attribute, value);
      lastValues.set(key, value);
      return true;
    }
    return false;
  }
  
  /**
   * Обновляет прогресс-бар
   */
  function updateProgressBar(selector, current, max, label = '') {
    const element = getCachedElement(selector);
    if (!element) return false;
    
    const percentage = max > 0 ? Math.min(100, Math.max(0, (current / max) * 100)) : 0;
    const barElement = element.querySelector('.progress-bar') || element;
    const labelElement = element.querySelector('.progress-label') || null;
    
    updateStyle(selector, 'width', `${percentage}%`);
    
    if (labelElement && label) {
      updateText(`${selector} .progress-label`, label);
    }
    
    return true;
  }
  
  /**
   * Обновляет список элементов (добавляет/удаляет только измененные)
   */
  function updateList(containerSelector, items, itemRenderer, itemKeyFn) {
    const container = getCachedElement(containerSelector);
    if (!container) return false;
    
    const key = `list:${containerSelector}`;
    const lastItems = lastValues.get(key) || [];
    const lastItemsMap = new Map(lastItems.map(item => [itemKeyFn(item), item]));
    const currentItemsMap = new Map(items.map(item => [itemKeyFn(item), item]));
    
    // Удаляем элементы, которых больше нет
    lastItems.forEach(item => {
      const itemKey = itemKeyFn(item);
      if (!currentItemsMap.has(itemKey)) {
        const element = container.querySelector(`[data-item-key="${itemKey}"]`);
        if (element) {
          element.remove();
        }
      }
    });
    
    // Добавляем/обновляем элементы
    items.forEach(item => {
      const itemKey = itemKeyFn(item);
      const lastItem = lastItemsMap.get(itemKey);
      const existingElement = container.querySelector(`[data-item-key="${itemKey}"]`);
      
      if (!existingElement) {
        // Создаем новый элемент
        const newElement = document.createElement('div');
        newElement.setAttribute('data-item-key', itemKey);
        newElement.innerHTML = itemRenderer(item);
        container.appendChild(newElement);
      } else if (!lastItem || JSON.stringify(lastItem) !== JSON.stringify(item)) {
        // Обновляем существующий элемент
        existingElement.innerHTML = itemRenderer(item);
      }
    });
    
    lastValues.set(key, items);
    return true;
  }
  
  /**
   * Очищает кэш
   */
  function clearCache() {
    elementCache.clear();
    lastValues.clear();
  }
  
  /**
   * Очищает кэш для конкретного селектора
   */
  function clearCacheFor(selector) {
    elementCache.delete(selector);
    const keysToDelete = [];
    lastValues.forEach((value, key) => {
      if (key.includes(selector)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => lastValues.delete(key));
  }
  
  return {
    updateText,
    updateHTML,
    updateClass,
    updateStyle,
    updateAttribute,
    updateProgressBar,
    updateList,
    getCachedElement,
    clearCache,
    clearCacheFor
  };
}





